/**
 * ulink-銷售 — 引流 + LINE 推播
 *
 * 功能：
 * 1. Facebook 廣告發布（→ 引流到 LINE/WhatsApp/官網）
 *    API: POST http://10.200.0.16:8765/api/v1/facebook/posts
 * 2. LINE AI Chatbot 廣播推播（→ 所有已加好友的用戶）
 *    API: POST https://alayaess.comboware.tw/api/v1/line/broadcast
 * 3. LINE 精準推播（指定用戶 ID）
 *    API: POST https://alayaess.comboware.tw/api/v1/line/push
 * 4. LINE 聯絡人名單
 *    API: GET  https://alayaess.comboware.tw/api/v1/line/contacts
 * 5. 推播歷史記錄
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Button, Input, Form, message, Tag, Space,
  Typography, Tabs, Table, Alert, Select, Modal, Badge,
  Divider, Statistic, Spin, Tooltip,
} from 'antd';
import {
  FacebookOutlined, SendOutlined, UserOutlined, HistoryOutlined,
  RocketOutlined, BulbOutlined, PictureOutlined, LinkOutlined,
  ThunderboltOutlined, TeamOutlined, ReloadOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// ── API config ────────────────────────────────────────────────────────────────
// FB API base loaded from system_configs (ULINK_FB_API_BASE)
let FB_API = 'http://10.200.0.16:8765/api/v1/facebook/posts';
// LINE API base — stored in system_configs as ULINK_LINE_API_BASE
// Default fallback to the OpenClaw relay
let LINE_BASE = 'http://10.66.99.82:18799/api/v1/line';

// These tokens come from the platform settings (stored in system_configs)
// Admin sets them in /ai-config or here directly
const SPO_API    = '/api/v1';
const spoH = () => ({ Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}`, 'Content-Type': 'application/json' });

// ── Token config (fetched from platform) ─────────────────────────────────────
async function getTokens() {
  try {
    const res = await fetch(`${SPO_API}/system-config/ulink-sales-tokens`, { headers: spoH() });
    if (res.ok) return await res.json();
  } catch { /* ignore */ }
  return {};
}

// ── Facebook Post ─────────────────────────────────────────────────────────────
function FacebookPostPanel({ fbToken }) {
  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [scheduling, setScheduling] = useState(false);

  const send = async (vals) => {
    if (!fbToken) { message.error('請先設定 Facebook API Token（設定分頁）'); return; }
    setSending(true);
    try {
      const body = {
        message: vals.message || '',
        image_url: vals.image_url || undefined,
        link_url: vals.link_url || undefined,
        publish_now: !scheduling,
        scheduled_at: scheduling && vals.scheduled_at ? vals.scheduled_at : undefined,
      };
      const res = await fetch(FB_API, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${fbToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (d.ok) {
        message.success(scheduling ? '排程貼文已建立！' : '✅ Facebook 貼文已發布！');
        setResult(d.post);
        form.resetFields();
      } else {
        message.error('發布失敗：' + (d.error || JSON.stringify(d)));
      }
    } catch (e) { message.error('連線失敗：' + e.message); }
    finally { setSending(false); }
  };

  return (
    <div>
      <Alert type="info" showIcon style={{ marginBottom: 16 }}
        message="Facebook 廣告發文 → 引流到 LINE / WhatsApp"
        description="發布後，貼文含你的 LINE / WhatsApp 連結，潛在客戶點擊後直接加入對話。" />
      <Form form={form} layout="vertical" onFinish={send}>
        <Form.Item name="message" label="貼文文字" rules={[{ required: true, message: '請輸入貼文內容' }]}>
          <TextArea rows={4} placeholder={`範例：\n🚀 ulink AI 解決方案 — 讓您的 IT 基礎架構升級！\n\n✅ NetAI 網路監控 AI\n✅ 一鍵部署、24/7 守護\n\n📲 立即私訊了解 → [LINE連結]`} />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="image_url" label={<><PictureOutlined /> EDM 圖片 URL（公開 HTTPS）</>}>
              <Input placeholder="https://your-cdn.com/banner.jpg" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="link_url" label={<><LinkOutlined /> 引流連結（LINE / 官網）</>}>
              <Input placeholder="https://line.me/ti/p/@yourbot 或官網" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12} align="middle">
          <Col>
            <Form.Item style={{ marginBottom: 0 }}>
              <Space>
                <Button type={scheduling ? 'default' : 'primary'} icon={<FacebookOutlined />}
                  onClick={() => { setScheduling(false); form.submit(); }}
                  loading={sending && !scheduling} style={{ background: scheduling ? undefined : '#1877f2', border: 'none' }}>
                  立即發布
                </Button>
                <Button icon={<HistoryOutlined />} onClick={() => setScheduling(s => !s)}
                  type={scheduling ? 'primary' : 'default'}>
                  {scheduling ? '✅ 排程模式' : '排程發布'}
                </Button>
              </Space>
            </Form.Item>
          </Col>
          {scheduling && (
            <Col>
              <Form.Item name="scheduled_at" label="排程時間" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                <Input type="datetime-local" style={{ width: 200 }} />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>

      {result && (
        <Card size="small" style={{ marginTop: 16, borderLeft: '4px solid #52c41a' }}>
          <Space direction="vertical" size={4}>
            <Text strong>✅ 發布成功</Text>
            <Text type="secondary">Status: <Tag color="green">{result.status}</Tag></Text>
            {result.facebook_post_id && (
              <Text type="secondary">Post ID: <Text code>{result.facebook_post_id}</Text></Text>
            )}
            {result.facebook_post_id && (
              <a href={`https://www.facebook.com/${result.facebook_post_id}`} target="_blank" rel="noreferrer">
                🔗 查看貼文
              </a>
            )}
          </Space>
        </Card>
      )}
    </div>
  );
}

// ── LINE Broadcast ─────────────────────────────────────────────────────────────
function LineBroadcastPanel({ lineToken }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [mode, setMode] = useState('text'); // text | template

  const broadcast = async () => {
    if (!lineToken) { message.error('請先設定 LINE API Token（設定分頁）'); return; }
    if (mode === 'text' && !text.trim()) { message.warning('請輸入推播內容'); return; }
    if (mode === 'template' && !templateName.trim()) { message.warning('請輸入模板名稱'); return; }
    setSending(true);
    try {
      const endpoint = mode === 'template'
        ? `${LINE_BASE}/push/template`
        : `${LINE_BASE}/broadcast`;
      const body = mode === 'template'
        ? { template_name: templateName }
        : { text: text.trim(), ...(filter ? { source_filter: filter } : {}) };
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${lineToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (d.ok) {
        message.success(`✅ LINE 推播成功！發送給 ${d.sent_to || 0} 人`);
        setResult(d);
        setText('');
      } else {
        message.error('推播失敗：' + (d.error || JSON.stringify(d)));
      }
    } catch (e) { message.error('連線失敗：' + e.message); }
    finally { setSending(false); }
  };

  return (
    <div>
      <Alert type="success" showIcon style={{ marginBottom: 16 }}
        message="LINE AI Chatbot 廣播推播"
        description="推播給所有曾與 LINE Bot 互動過的用戶（透過 AlayaEss 平台）。AI Bot 會自動回覆後續對話。" />

      <Space style={{ marginBottom: 12 }}>
        <Button type={mode === 'text' ? 'primary' : 'default'} onClick={() => setMode('text')}>✏️ 自訂訊息</Button>
        <Button type={mode === 'template' ? 'primary' : 'default'} onClick={() => setMode('template')}>📄 使用模板</Button>
      </Space>

      {mode === 'text' ? (
        <>
          <TextArea rows={4} value={text} onChange={e => setText(e.target.value)}
            placeholder={`範例：\n親愛的 ulink 客戶您好 👋\n\n我們最新推出 NetAI — AI 智慧網路監控，舊客戶專屬優惠！\n\n🔗 了解詳情 → https://netai.comboware.tw\n📞 立即預約顧問`}
            style={{ marginBottom: 12 }} />
          <Select value={filter} onChange={setFilter} placeholder="推播對象（預設全部）"
            style={{ width: 200, marginBottom: 12 }} allowClear>
            <Select.Option value="">全部用戶</Select.Option>
            <Select.Option value="user">僅個人對話</Select.Option>
            <Select.Option value="group">僅群組</Select.Option>
          </Select>
        </>
      ) : (
        <Input value={templateName} onChange={e => setTemplateName(e.target.value)}
          placeholder="模板名稱（在 AlayaEss Portal → LINE → 推播模板 設定）"
          style={{ marginBottom: 12 }} />
      )}

      <Button type="primary" icon={<SendOutlined />} loading={sending} onClick={broadcast}
        style={{ background: '#06c755', border: 'none' }} block>
        LINE 廣播推播
      </Button>

      {result && (
        <Card size="small" style={{ marginTop: 12, borderLeft: '4px solid #06c755' }}>
          <Statistic title="成功推播人數" value={result.sent_to || 0}
            suffix={`/ ${result.total_targets || result.sent_to || 0}`} valueStyle={{ color: '#06c755' }} />
        </Card>
      )}
    </div>
  );
}

// ── LINE Push to specific user ─────────────────────────────────────────────────
function LinePushPanel({ lineToken, contacts }) {
  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);

  const push = async (vals) => {
    if (!lineToken) { message.error('請先設定 LINE API Token（設定分頁）'); return; }
    setSending(true);
    try {
      const res = await fetch(`${LINE_BASE}/push`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${lineToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: vals.to, text: vals.text }),
      });
      const d = await res.json();
      if (d.ok) { message.success('✅ 訊息已送出！'); form.resetFields(['text']); }
      else message.error('推播失敗：' + (d.error || JSON.stringify(d)));
    } catch (e) { message.error('連線失敗：' + e.message); }
    finally { setSending(false); }
  };

  return (
    <div>
      <Alert type="warning" showIcon style={{ marginBottom: 16 }}
        message="LINE 精準推播（手動指定對象）"
        description="輸入特定 LINE User ID 或從下方聯絡人選取，發送個人化訊息。" />
      <Form form={form} layout="vertical" onFinish={push}>
        <Form.Item name="to" label="LINE ID" rules={[{ required: true }]}>
          <Select showSearch placeholder="選擇聯絡人或輸入 LINE User ID"
            optionFilterProp="children" allowClear>
            {contacts.map(c => (
              <Select.Option key={c.id} value={c.id}>
                <Tag color={c.source_type === 'group' ? 'blue' : 'green'}>{c.source_type}</Tag>
                {c.id.slice(0, 20)}... ({c.message_count} 則)
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="text" label="訊息內容" rules={[{ required: true }]}>
          <TextArea rows={3} placeholder="您好！感謝您對 ulink 服務的關注..." />
        </Form.Item>
        <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={sending}
          style={{ background: '#06c755', border: 'none' }} block>
          送出訊息
        </Button>
      </Form>
    </div>
  );
}

// ── Contacts ───────────────────────────────────────────────────────────────────
function ContactsPanel({ lineToken, contacts, loading, onRefresh }) {
  const cols = [
    { title: 'LINE ID', dataIndex: 'id', ellipsis: true,
      render: id => <Text code style={{ fontSize: 11 }}>{id}</Text> },
    { title: '類型', dataIndex: 'source_type', width: 70,
      render: t => <Tag color={t === 'group' ? 'blue' : 'green'}>{t}</Tag> },
    { title: '訊息數', dataIndex: 'message_count', width: 80, sorter: (a,b) => a.message_count - b.message_count },
    { title: '最後互動', dataIndex: 'last_at', width: 140,
      render: v => v ? new Date(v).toLocaleString('zh-TW') : '-' },
  ];
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <Text type="secondary">LINE Bot 聯絡人名單（曾互動的用戶）</Text>
        <Button size="small" icon={<ReloadOutlined />} onClick={onRefresh} loading={loading}>更新</Button>
      </div>
      <Table size="small" dataSource={contacts} rowKey="id" columns={cols}
        loading={loading} pagination={{ pageSize: 20 }} />
    </div>
  );
}

// ── Token Settings ─────────────────────────────────────────────────────────────
function SettingsPanel({ fbToken, setFbToken, lineToken, setLineToken, onSave }) {
  return (
    <div>
      <Alert type="info" showIcon style={{ marginBottom: 16 }}
        message="API Token 設定"
        description="Token 儲存於此平台的 system_configs，不會外洩到前端。" />
      <Form layout="vertical">
        <Form.Item label={<><FacebookOutlined style={{ color: '#1877f2' }} /> Facebook Post API Token</>}
          extra={<a href="http://10.200.0.16:8765" target="_blank" rel="noreferrer">前往 10.200.0.16:8765 取得 Token →</a>}>
          <Input.Password value={fbToken} onChange={e => setFbToken(e.target.value)}
            placeholder="從 10.200.0.16:8765 後台 → API 權限 取得" />
        </Form.Item>
        <Form.Item label={<><span style={{ color: '#06c755' }}>LINE</span> AlayaEss API Token</>}
          extra={<a href="https://alayaess.comboware.tw" target="_blank" rel="noreferrer">前往 AlayaEss Portal → Bot → 🔑 Token →</a>}>
          <Input.Password value={lineToken} onChange={e => setLineToken(e.target.value)}
            placeholder="dak_live_xxx (需含 line_push + line_history scope)" />
        </Form.Item>
        <Button type="primary" icon={<SendOutlined />} onClick={onSave} block>儲存 Token</Button>
      </Form>
      <Divider />
      <Title level={5}>API 參考</Title>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Card size="small" title={<><FacebookOutlined style={{ color: '#1877f2' }} /> Facebook Post API</>}>
          <Text code>POST http://10.200.0.16:8765/api/v1/facebook/posts</Text><br />
          <Text type="secondary" style={{ fontSize: 12 }}>支援立即發布 / 排程。image_url 需為公開 HTTPS。</Text><br />
          <a href="http://10.200.0.16:8765/devdocs/facebook-post-api" target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>查看完整文件 →</a>
        </Card>
        <Card size="small" title={<><span style={{ color: '#06c755' }}>LINE</span> AlayaEss Push API</>}>
          <Text code>POST https://alayaess.comboware.tw/api/v1/line/broadcast</Text><br />
          <Text type="secondary" style={{ fontSize: 12 }}>廣播到所有已知用戶。AI Bot 自動回覆後續對話。</Text><br />
          <a href="http://10.66.99.82:18799/devdoc/6a3a42c9ab2323e94ddd1704" target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>查看完整文件 →</a>
        </Card>
      </Space>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function UlinkSalesPage() {
  const [fbToken, setFbToken] = useState('');
  const [lineToken, setLineToken] = useState('');
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  // Load saved tokens from platform
  useEffect(() => {
    fetch(`${SPO_API}/system-config/list`, { headers: spoH() })
      .then(r => r.ok ? r.json() : [])
      .then(configs => {
        const cfgMap = {};
        (configs || []).forEach(c => { cfgMap[c.key] = c.value; });
        if (cfgMap['ULINK_FB_TOKEN']) setFbToken(cfgMap['ULINK_FB_TOKEN']);
        if (cfgMap['ULINK_LINE_TOKEN']) setLineToken(cfgMap['ULINK_LINE_TOKEN']);
        if (cfgMap['ULINK_LINE_API_BASE']) LINE_BASE = cfgMap['ULINK_LINE_API_BASE'] + '/api/v1/line';
        if (cfgMap['ULINK_FB_API_BASE']) FB_API = cfgMap['ULINK_FB_API_BASE'] + '/api/v1/facebook/posts';
      }).catch(() => {});
  }, []);

  const saveTokens = async () => {
    try {
      await Promise.all([
        fetch(`${SPO_API}/system-config`, { method: 'POST', headers: spoH(),
          body: JSON.stringify({ key: 'ULINK_FB_TOKEN', value: fbToken }) }),
        fetch(`${SPO_API}/system-config`, { method: 'POST', headers: spoH(),
          body: JSON.stringify({ key: 'ULINK_LINE_TOKEN', value: lineToken }) }),
      ]);
      message.success('Token 已儲存');
    } catch (e) { message.error('儲存失敗: ' + e.message); }
  };

  const fetchContacts = useCallback(async () => {
    if (!lineToken) return;
    setContactsLoading(true);
    try {
      const res = await fetch(`${LINE_BASE}/contacts?limit=500`, {
        headers: { 'Authorization': `Bearer ${lineToken}` }
      });
      const d = await res.json();
      if (d.ok) setContacts(d.contacts || []);
      else message.error('無法取得聯絡人：' + (d.error || ''));
    } catch (e) { message.error('連線失敗：' + e.message); }
    finally { setContactsLoading(false); }
  }, [lineToken]);

  useEffect(() => { if (lineToken) fetchContacts(); }, [lineToken, fetchContacts]);

  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <Title level={3} style={{ margin: 0 }}>
          <RocketOutlined style={{ marginRight: 8, color: '#722ed1' }} />
          ulink-銷售 — 引流 + LINE 推播
        </Title>
        <Text type="secondary">
          Facebook 廣告引流 → LINE AI Chatbot 互動 → 手動精準推播
        </Text>
      </div>

      {/* Stats */}
      <Row gutter={12} style={{ marginBottom: 20 }}>
        <Col span={6}>
          <Card size="small" style={{ borderTop: '3px solid #1877f2' }}>
            <Statistic title="LINE 聯絡人" value={contacts.length}
              prefix={<TeamOutlined style={{ color: '#1877f2' }} />}
              valueStyle={{ color: '#1877f2' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={{ borderTop: '3px solid #06c755' }}>
            <Statistic title="個人對話" value={contacts.filter(c => c.source_type === 'user').length}
              prefix={<UserOutlined style={{ color: '#06c755' }} />}
              valueStyle={{ color: '#06c755' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={{ borderTop: '3px solid #1877f2' }}>
            <Statistic title="FB Token"
              value={fbToken ? '✅ 已設定' : '❌ 未設定'}
              valueStyle={{ color: fbToken ? '#52c41a' : '#ff4d4f', fontSize: 18 }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={{ borderTop: '3px solid #06c755' }}>
            <Statistic title="LINE Token"
              value={lineToken ? '✅ 已設定' : '❌ 未設定'}
              valueStyle={{ color: lineToken ? '#52c41a' : '#ff4d4f', fontSize: 18 }} />
          </Card>
        </Col>
      </Row>

      {(!fbToken || !lineToken) && (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }}
          message="請先設定 API Token"
          description="到「Token 設定」分頁填入 Facebook 和 LINE 的 API Token 才能使用推播功能。" />
      )}

      <Tabs items={[
        {
          key: 'fb',
          label: <span><FacebookOutlined style={{ color: '#1877f2' }} /> Facebook 發文引流</span>,
          children: <FacebookPostPanel fbToken={fbToken} />,
        },
        {
          key: 'broadcast',
          label: (
            <span>
              <span style={{ color: '#06c755', fontWeight: 700 }}>LINE</span>
              {' '}廣播推播
              {contacts.length > 0 && <Badge count={contacts.length} color="#06c755" style={{ marginLeft: 6 }} />}
            </span>
          ),
          children: <LineBroadcastPanel lineToken={lineToken} />,
        },
        {
          key: 'push',
          label: <span><SendOutlined /> LINE 精準推播</span>,
          children: <LinePushPanel lineToken={lineToken} contacts={contacts} />,
        },
        {
          key: 'contacts',
          label: <span><TeamOutlined /> LINE 聯絡人</span>,
          children: <ContactsPanel lineToken={lineToken} contacts={contacts}
            loading={contactsLoading} onRefresh={fetchContacts} />,
        },
        {
          key: 'settings',
          label: <span>⚙️ Token 設定</span>,
          children: <SettingsPanel fbToken={fbToken} setFbToken={setFbToken}
            lineToken={lineToken} setLineToken={setLineToken} onSave={saveTokens} />,
        },
      ]} />
    </div>
  );
}
